import React, { useState, useMemo, useEffect } from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import "./BacktestPage.css";

// ─── Theme hook ───────────────────────────────────────────────
function readThemeColors() {
  const s = getComputedStyle(document.documentElement);
  return {
    primary:    s.getPropertyValue('--rv-primary').trim()      || '#b8ff3c',
    primaryRgb: s.getPropertyValue('--rv-primary-rgb').trim()  || '184,255,60',
    line:       s.getPropertyValue('--rv-line').trim()         || 'rgba(255,255,255,0.07)',
    inkFaint:   s.getPropertyValue('--rv-ink-faint').trim()    || 'rgba(255,255,255,0.35)',
    surface:    s.getPropertyValue('--rv-surface-raised').trim() || '#0d0d0d',
  };
}

function useThemeColor() {
  const [tc, setTc] = useState(readThemeColors);
  useEffect(() => {
    const obs = new MutationObserver(() => setTc(readThemeColors()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return tc;
}

// ─── Constants ────────────────────────────────────────────────
const PERIODS = [
  { label: "3M", value: "3mo" },
  { label: "6M", value: "6mo" },
  { label: "9M", value: "9mo" },
  { label: "1Y", value: "1y"  },
  { label: "2Y", value: "2y"  },
  { label: "3Y", value: "3y"  },
  { label: "5Y", value: "5y"  },
];

const STRATEGY_TYPES = ["LIMIT", "LIMIT_PERCENT", "LOOKBACK_LIMIT", "SMA_CROSS", "RSI_MEAN_REVERT"];

const LOOKBACK_TYPES = [
  { value: "DAY_LOWS_LOWEST",  label: "Lowest of day lows"  },
  { value: "DAY_LOWEST_HI",    label: "Highest of day lows" },
  { value: "DAY_HIGHS_LOWEST", label: "Lowest of day highs" },
  { value: "DAY_HIGHS_HI",     label: "Highest of day highs"},
  { value: "DAY_AVG_LOWEST",   label: "Lowest of day avgs"  },
  { value: "DAY_AVG_HI",       label: "Highest of day avgs" },
];

const DEFAULT_PARAMS = {
  LIMIT:          { minPrice: "", maxPrice: "" },
  LIMIT_PERCENT:  { minPrice: "", profitPercentage: "" },
  LOOKBACK_LIMIT: { lookbackDays: "5", lookbackType: "DAY_LOWS_LOWEST",
                    lowerPricePercentage: "0.5", buyCeiling: "", buyFloor: "",
                    profitPercentage: "" },
  SMA_CROSS:      { fastPeriod: "50", slowPeriod: "200" },
  RSI_MEAN_REVERT:{ rsiPeriod: "14", oversoldThreshold: "30", overboughtThreshold: "60" },
};

const STRATEGY_INFO = {
  LIMIT: {
    title: "Buy Low, Sell High — Fixed Prices",
    summary: "You pick a price to buy at and a price to sell at. The app watches the stock and automatically buys when it drops to your target, then sells when it rises to your exit price.",
    bullets: [
      "Great starting point — easy to understand and reason about",
      "Works well for stocks that bounce up and down in a predictable range",
      "The app does nothing if the price never hits either of your levels",
    ],
  },
  LIMIT_PERCENT: {
    title: "Buy Low, Sell at a % Gain",
    summary: "You set a price to buy at, and instead of picking a sell price in dollars, you pick how much profit you want (e.g. 2%). The app sells automatically once it hits that gain.",
    bullets: [
      "Easier than guessing an exact sell price — just decide your profit goal",
      "Good for stocks you expect to bounce after a dip",
      "After selling, it waits for the next dip and buys again",
    ],
  },
  LOOKBACK_LIMIT: {
    title: "Buy at a Smart Dip — Based on Recent Prices",
    summary: "Instead of a fixed buy price, the app looks at the stock's recent lows (e.g. the past 5 days) and calculates a smart buy point just below that. It adapts as the stock moves rather than using a number you set once.",
    bullets: [
      "More flexible than a fixed buy price — adjusts to what the stock is doing now",
      "You can set a floor and ceiling to avoid buying during extreme crashes or spikes",
      "Sell when the stock gains your target percentage, same as the strategy above",
    ],
  },
  SMA_CROSS: {
    title: "SMA Cross — Follow the Trend",
    summary: "SMA stands for Simple Moving Average — it's just the average price of a stock over a set number of days. This strategy tracks two SMAs: a faster short-term one (e.g. 50 days) and a slower long-term one (e.g. 200 days). When the short-term average crosses above the long-term average, momentum is building — that's the buy signal. When it crosses back below, the trend is fading — that's the sell.",
    bullets: [
      "The 50/200-day cross is one of the most widely followed signals on Wall Street",
      "Works best when a stock is in a clear upward or downward trend",
      "Tends to hold positions for weeks or months — not a frequent trader",
    ],
  },
  RSI_MEAN_REVERT: {
    title: "RSI Mean Reversion — Buy the Panic, Sell the Hype",
    summary: "RSI (Relative Strength Index) is a 0–100 score that measures how hard a stock has been pushed up or down recently. Below 30 means it's been heavily sold off — likely oversold. Above 60–70 means it's been bid up sharply — likely overbought. This strategy buys when the RSI drops into oversold territory, expecting a bounce back, and sells when it climbs into overbought territory.",
    bullets: [
      "Works well for stable stocks like big banks, utilities, or consumer brands",
      "The logic: markets overreact, and prices tend to snap back toward their normal range",
      "Lower buy threshold (e.g. 25 instead of 30) means you wait for a bigger sell-off before buying",
    ],
  },
};

// Indicator definitions — color, whether it needs a separate panel
const INDICATORS = [
  { key: "sma20",  label: "SMA 20",  color: "#f59e0b", separate: false },
  { key: "sma50",  label: "SMA 50",  color: "#06b6d4", separate: false },
  { key: "sma100", label: "SMA 100", color: "#a855f7", separate: false },
  { key: "sma200", label: "SMA 200", color: "#ec4899", separate: false },
  { key: "ema12",  label: "EMA 12",  color: "#fbbf24", separate: false },
  { key: "ema26",  label: "EMA 26",  color: "#34d399", separate: false },
  { key: "rsi14",  label: "RSI 14",  color: "#818cf8", separate: true  },
];

// ─── Indicator math ────────────────────────────────────────────
function calcSMA(closes, period) {
  return closes.map((_, i) => {
    if (i < period - 1) return null;
    return closes.slice(i - period + 1, i + 1).reduce((s, v) => s + v, 0) / period;
  });
}

function calcEMA(closes, period) {
  const k = 2 / (period + 1);
  const out = new Array(closes.length).fill(null);
  if (closes.length < period) return out;
  out[period - 1] = closes.slice(0, period).reduce((s, v) => s + v, 0) / period;
  for (let i = period; i < closes.length; i++) {
    out[i] = closes[i] * k + out[i - 1] * (1 - k);
  }
  return out;
}

function calcRSI(closes, period = 14) {
  const out = new Array(closes.length).fill(null);
  if (closes.length < period + 1) return out;
  const changes = closes.slice(1).map((c, i) => c - closes[i]);
  let avgGain = 0, avgLoss = 0;
  for (let i = 0; i < period; i++) {
    if (changes[i] > 0) avgGain += changes[i];
    else avgLoss += Math.abs(changes[i]);
  }
  avgGain /= period;
  avgLoss /= period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period; i < changes.length; i++) {
    const gain = Math.max(changes[i], 0);
    const loss = Math.max(-changes[i], 0);
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i + 1] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

function computeIndicatorArrays(closes, activeIndicators) {
  const out = {};
  if (activeIndicators.sma20)  out.sma20  = calcSMA(closes, 20);
  if (activeIndicators.sma50)  out.sma50  = calcSMA(closes, 50);
  if (activeIndicators.sma100) out.sma100 = calcSMA(closes, 100);
  if (activeIndicators.sma200) out.sma200 = calcSMA(closes, 200);
  if (activeIndicators.ema12)  out.ema12  = calcEMA(closes, 12);
  if (activeIndicators.ema26)  out.ema26  = calcEMA(closes, 26);
  if (activeIndicators.rsi14)  out.rsi14  = calcRSI(closes, 14);
  return out;
}

// ─── Data fetching ─────────────────────────────────────────────
const API_BASE = "https://18z7zyxvn4.execute-api.us-east-2.amazonaws.com";

async function fetchHistoricalData(symbol, period) {
  const token = sessionStorage.getItem("jwt");
  if (!token) throw new Error("Not authenticated. Please log in and try again.");
  const url = `${API_BASE}/robovest-api/market-history?symbol=${encodeURIComponent(symbol.toUpperCase())}&period=${encodeURIComponent(period)}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `HTTP ${res.status}`);
  }
  const { bars } = await res.json();
  if (!bars || bars.length === 0) throw new Error("No price data found for that symbol.");
  return bars;
}

// ─── Backtesting engine ────────────────────────────────────────
function getLookbackThreshold(slice, lookbackType) {
  switch (lookbackType) {
    case "DAY_LOWS_LOWEST":  return Math.min(...slice.map(d => d.low));
    case "DAY_LOWEST_HI":    return Math.max(...slice.map(d => d.low));
    case "DAY_HIGHS_LOWEST": return Math.min(...slice.map(d => d.high));
    case "DAY_HIGHS_HI":     return Math.max(...slice.map(d => d.high));
    case "DAY_AVG_LOWEST":   return Math.min(...slice.map(d => (d.high + d.low) / 2));
    case "DAY_AVG_HI":       return Math.max(...slice.map(d => (d.high + d.low) / 2));
    default:                 return Math.min(...slice.map(d => d.low));
  }
}

function runBacktest(priceData, strategy, startingCapital) {
  const closes = priceData.map(d => d.close);
  const fastSMA = strategy.type === "SMA_CROSS"
    ? calcSMA(closes, parseInt(strategy.fastPeriod)) : null;
  const slowSMA = strategy.type === "SMA_CROSS"
    ? calcSMA(closes, parseInt(strategy.slowPeriod)) : null;
  const rsiArr = strategy.type === "RSI_MEAN_REVERT"
    ? calcRSI(closes, parseInt(strategy.rsiPeriod)) : null;

  let cash = startingCapital;
  let shares = 0;
  let position = null;
  const trades = [];
  const portfolioHistory = [];

  for (let i = 0; i < priceData.length; i++) {
    const day = priceData[i];
    const price = day.close;

    if (position) {
      let shouldSell = false;
      if (strategy.type === "LIMIT") {
        shouldSell = price >= parseFloat(strategy.maxPrice);
      } else if (strategy.type === "LIMIT_PERCENT" || strategy.type === "LOOKBACK_LIMIT") {
        const target = position.buyPrice * (1 + parseFloat(strategy.profitPercentage) / 100);
        shouldSell = price >= target;
      } else if (strategy.type === "SMA_CROSS") {
        // death cross: fast SMA crosses below slow SMA
        shouldSell = i > 0
          && fastSMA[i] != null && slowSMA[i] != null
          && fastSMA[i - 1] != null && slowSMA[i - 1] != null
          && fastSMA[i] < slowSMA[i] && fastSMA[i - 1] >= slowSMA[i - 1];
      } else if (strategy.type === "RSI_MEAN_REVERT") {
        shouldSell = rsiArr[i] != null && rsiArr[i] > parseFloat(strategy.overboughtThreshold);
      }
      if (shouldSell) {
        const profit = (price - position.buyPrice) * position.shares;
        cash += position.shares * price;
        trades.push({
          type: "SELL", date: day.date, dateIndex: i, price, shares: position.shares,
          profit, returnPct: ((price - position.buyPrice) / position.buyPrice * 100).toFixed(2),
        });
        shares = 0;
        position = null;
      }
    }

    if (!position && cash >= price) {
      let shouldBuy = false;
      if (strategy.type === "LIMIT") {
        shouldBuy = price <= parseFloat(strategy.minPrice);
      } else if (strategy.type === "LIMIT_PERCENT") {
        shouldBuy = price <= parseFloat(strategy.minPrice);
      } else if (strategy.type === "LOOKBACK_LIMIT") {
        const lb = parseInt(strategy.lookbackDays);
        if (i >= lb) {
          let threshold = getLookbackThreshold(priceData.slice(i - lb, i), strategy.lookbackType);
          threshold *= (1 - parseFloat(strategy.lowerPricePercentage || 0) / 100);
          if (strategy.buyFloor)   threshold = Math.max(threshold, parseFloat(strategy.buyFloor));
          if (strategy.buyCeiling) threshold = Math.min(threshold, parseFloat(strategy.buyCeiling));
          shouldBuy = price <= threshold;
        }
      } else if (strategy.type === "SMA_CROSS") {
        // golden cross: fast SMA crosses above slow SMA
        shouldBuy = i > 0
          && fastSMA[i] != null && slowSMA[i] != null
          && fastSMA[i - 1] != null && slowSMA[i - 1] != null
          && fastSMA[i] > slowSMA[i] && fastSMA[i - 1] <= slowSMA[i - 1];
      } else if (strategy.type === "RSI_MEAN_REVERT") {
        shouldBuy = rsiArr[i] != null && rsiArr[i] < parseFloat(strategy.oversoldThreshold);
      }
      if (shouldBuy) {
        const sharesToBuy = Math.floor(cash / price);
        if (sharesToBuy > 0) {
          cash -= sharesToBuy * price;
          shares = sharesToBuy;
          position = { buyPrice: price, date: day.date, dateIndex: i, shares: sharesToBuy };
          trades.push({ type: "BUY", date: day.date, dateIndex: i, price, shares: sharesToBuy });
        }
      }
    }

    portfolioHistory.push({ date: day.date, price, portfolioValue: cash + shares * price });
  }

  const finalValue = cash + shares * (priceData.at(-1)?.close ?? 0);
  let peak = startingCapital, maxDD = 0;
  portfolioHistory.forEach(p => {
    if (p.portfolioValue > peak) peak = p.portfolioValue;
    const dd = (peak - p.portfolioValue) / peak * 100;
    if (dd > maxDD) maxDD = dd;
  });
  const sells = trades.filter(t => t.type === "SELL");
  const wins  = sells.filter(t => t.profit > 0);
  return {
    trades, portfolioHistory, finalValue, openPosition: position,
    stats: {
      totalReturn:  ((finalValue - startingCapital) / startingCapital * 100).toFixed(2),
      numTrades:    sells.length,
      winRate:      sells.length ? ((wins.length / sells.length) * 100).toFixed(1) : "—",
      maxDrawdown:  maxDD.toFixed(2),
    },
  };
}

// ─── Chart helpers ─────────────────────────────────────────────
const fmtCurrency = v => v == null ? "" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = v => `${v}%`;
const round2 = v => v != null ? +v.toFixed(2) : null;

function buildChartData(portfolioHistory, trades, priceData, activeIndicators) {
  const byIndex = {};
  trades.forEach(t => { byIndex[t.dateIndex] = t; });
  const closes = priceData.map(d => d.close);
  const indArrays = computeIndicatorArrays(closes, activeIndicators);
  return portfolioHistory.map((d, i) => {
    const pt = {
      ...d,
      buySignal:  byIndex[i]?.type === "BUY"  ? d.price : undefined,
      sellSignal: byIndex[i]?.type === "SELL" ? d.price : undefined,
    };
    Object.keys(indArrays).forEach(k => { pt[k] = round2(indArrays[k][i]); });
    return pt;
  });
}

// Trade signal dots
const TradeDot = ({ cx, cy, payload, primaryColor = '#b8ff3c' }) => {
  if (payload.buySignal  !== undefined) return <polygon key={`b${cx}`} points={`${cx},${cy-10} ${cx-7},${cy+4} ${cx+7},${cy+4}`} fill={primaryColor} />;
  if (payload.sellSignal !== undefined) return <polygon key={`s${cx}`} points={`${cx},${cy+10} ${cx-7},${cy-4} ${cx+7},${cy-4}`} fill="#f43f5e" />;
  return <g key={`d${cx}`} />;
};

const PriceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const activeLines = INDICATORS.filter(ind => d?.[ind.key] != null);
  return (
    <div className="bt-tooltip">
      <div className="bt-tooltip-date">{label}</div>
      <div className="bt-tooltip-row"><span>Price</span><span>{fmtCurrency(d?.price)}</span></div>
      {activeLines.filter(i => !i.separate).map(ind => (
        <div key={ind.key} className="bt-tooltip-row">
          <span style={{ color: ind.color }}>{ind.label}</span>
          <span>{fmtCurrency(d[ind.key])}</span>
        </div>
      ))}
      {d?.buySignal  !== undefined && <div className="bt-tooltip-signal buy">▲ BUY @ {fmtCurrency(d.buySignal)}</div>}
      {d?.sellSignal !== undefined && <div className="bt-tooltip-signal sell">▼ SELL @ {fmtCurrency(d.sellSignal)}</div>}
    </div>
  );
};

const RSITooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bt-tooltip">
      <div className="bt-tooltip-date">{label}</div>
      <div className="bt-tooltip-row"><span style={{ color: "#818cf8" }}>RSI 14</span><span>{payload[0]?.value?.toFixed(1)}</span></div>
    </div>
  );
};

// ─── Indicator Toolbar ─────────────────────────────────────────
const IndicatorToolbar = ({ activeIndicators, onToggle }) => (
  <div className="bt-ind-toolbar">
    <span className="bt-ind-label">Indicators</span>
    <div className="bt-ind-chips">
      {INDICATORS.map(ind => (
        <button
          key={ind.key}
          className={`bt-ind-chip${activeIndicators[ind.key] ? " active" : ""}`}
          style={activeIndicators[ind.key] ? { borderColor: ind.color, color: ind.color, background: `${ind.color}18` } : {}}
          onClick={() => onToggle(ind.key)}
        >
          <span
            className="bt-ind-dot"
            style={{ background: activeIndicators[ind.key] ? ind.color : "var(--rv-line)" }}
          />
          {ind.label}
          {ind.separate && <span className="bt-ind-sep-tag">panel</span>}
        </button>
      ))}
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────
const BacktestPage = () => {
  const tc = useThemeColor();
  const [symbol,           setSymbol]           = useState("SPY");
  const [strategyType,     setStrategyType]     = useState("LIMIT_PERCENT");
  const [params,           setParams]           = useState({ ...DEFAULT_PARAMS.LIMIT_PERCENT });
  const [period,           setPeriod]           = useState("1y");
  const [capital,          setCapital]          = useState("10000");
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState("");
  const [backtestCore,     setBacktestCore]     = useState(null);
  const [rawPriceData,     setRawPriceData]     = useState(null);
  const [activeIndicators,      setActiveIndicators]      = useState({});
  const [strategyInfoDismissed, setStrategyInfoDismissed] = useState(false);

  const setParam = (key, val) => setParams(p => ({ ...p, [key]: val }));

  const handleStrategyChange = (type) => {
    setStrategyType(type);
    setParams({ ...DEFAULT_PARAMS[type] });
    setBacktestCore(null);
    setError("");
    setStrategyInfoDismissed(false);
  };

  const toggleIndicator = (key) =>
    setActiveIndicators(prev => ({ ...prev, [key]: !prev[key] }));

  const handleRun = async () => {
    setError("");
    setBacktestCore(null);
    setRawPriceData(null);
    setLoading(true);
    try {
      const data = await fetchHistoricalData(symbol.trim(), period);
      if (!data.length) throw new Error("No price data found.");
      const res = runBacktest(data, { type: strategyType, ...params }, parseFloat(capital) || 10000);
      setRawPriceData(data);
      setBacktestCore(res);
    } catch (e) {
      setError(e.message || "Failed to fetch data. Check the symbol and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Recompute chart data whenever core results or active indicators change
  const chartData = useMemo(() => {
    if (!backtestCore || !rawPriceData) return [];
    return buildChartData(backtestCore.portfolioHistory, backtestCore.trades, rawPriceData, activeIndicators);
  }, [backtestCore, rawPriceData, activeIndicators]);

  const hasResult     = backtestCore && chartData.length > 0;
  const statPositive  = hasResult && parseFloat(backtestCore.stats.totalReturn) >= 0;
  const showRSI       = activeIndicators.rsi14;
  const overlayInds   = INDICATORS.filter(ind => !ind.separate && activeIndicators[ind.key]);

  return (
    <div className="bt-page">
      <div className="bt-header">
        <div className="bt-eyebrow">Strategy Lab</div>
        <h1 className="bt-title">Backtester</h1>
        <p className="bt-subtitle">Simulate how your strategy would have performed on historical data.</p>
      </div>

      <div className="bt-layout">
        {/* ── Config Panel ──────────────────────────────── */}
        <aside className="bt-config">
          <div className="bt-config-section">
            <label className="bt-label">Symbol</label>
            <input className="bt-input" value={symbol}
              onChange={e => setSymbol(e.target.value.toUpperCase())}
              placeholder="e.g. SPY, AAPL, TSLA" />
          </div>

          <div className="bt-config-section">
            <label className="bt-label">Strategy Type</label>
            <div className="bt-radio-group">
              {STRATEGY_TYPES.map(t => {
                const comingSoon = t === "SMA_CROSS" || t === "RSI_MEAN_REVERT";
                return (
                  <button key={t}
                    className={`bt-radio-btn${strategyType === t ? " active" : ""}`}
                    onClick={() => handleStrategyChange(t)}>
                    {t.replace(/_/g, " ")}
                    {comingSoon && <span className="bt-coming-soon">preview</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bt-config-section bt-params">
            <label className="bt-label">Parameters</label>

            {strategyType === "LIMIT" && <>
              <div className="bt-field">
                <label>Buy Below ($)</label>
                <input className="bt-input" type="number" placeholder="e.g. 450.00"
                  value={params.minPrice} onChange={e => setParam("minPrice", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Sell Above ($)</label>
                <input className="bt-input" type="number" placeholder="e.g. 470.00"
                  value={params.maxPrice} onChange={e => setParam("maxPrice", e.target.value)} />
              </div>
            </>}

            {strategyType === "LIMIT_PERCENT" && <>
              <div className="bt-field">
                <label>Buy Below ($)</label>
                <input className="bt-input" type="number" placeholder="e.g. 450.00"
                  value={params.minPrice} onChange={e => setParam("minPrice", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Profit Target (%)</label>
                <input className="bt-input" type="number" placeholder="e.g. 1.5"
                  value={params.profitPercentage} onChange={e => setParam("profitPercentage", e.target.value)} />
              </div>
            </>}

            {strategyType === "LOOKBACK_LIMIT" && <>
              <div className="bt-field">
                <label>Lookback Days</label>
                <input className="bt-input" type="number" placeholder="e.g. 5"
                  value={params.lookbackDays} onChange={e => setParam("lookbackDays", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Lookback Type</label>
                <select className="bt-input bt-select" value={params.lookbackType}
                  onChange={e => setParam("lookbackType", e.target.value)}>
                  {LOOKBACK_TYPES.map(lt => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </select>
              </div>
              <div className="bt-field">
                <label>Lower Price % (offset)</label>
                <input className="bt-input" type="number" placeholder="e.g. 0.5"
                  value={params.lowerPricePercentage} onChange={e => setParam("lowerPricePercentage", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Buy Ceiling ($)</label>
                <input className="bt-input" type="number" placeholder="optional"
                  value={params.buyCeiling} onChange={e => setParam("buyCeiling", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Buy Floor ($)</label>
                <input className="bt-input" type="number" placeholder="optional"
                  value={params.buyFloor} onChange={e => setParam("buyFloor", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Profit Target (%)</label>
                <input className="bt-input" type="number" placeholder="e.g. 1.5"
                  value={params.profitPercentage} onChange={e => setParam("profitPercentage", e.target.value)} />
              </div>
            </>}

            {strategyType === "SMA_CROSS" && <>
              <div className="bt-field">
                <label>Fast SMA Period</label>
                <input className="bt-input" type="number" placeholder="e.g. 50"
                  value={params.fastPeriod} onChange={e => setParam("fastPeriod", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Slow SMA Period</label>
                <input className="bt-input" type="number" placeholder="e.g. 200"
                  value={params.slowPeriod} onChange={e => setParam("slowPeriod", e.target.value)} />
              </div>
              <p className="bt-hint">Buys on golden cross (fast crosses above slow), sells on death cross.</p>
            </>}

            {strategyType === "RSI_MEAN_REVERT" && <>
              <div className="bt-field">
                <label>RSI Period</label>
                <input className="bt-input" type="number" placeholder="e.g. 14"
                  value={params.rsiPeriod} onChange={e => setParam("rsiPeriod", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Buy below RSI (oversold)</label>
                <input className="bt-input" type="number" placeholder="e.g. 30"
                  value={params.oversoldThreshold} onChange={e => setParam("oversoldThreshold", e.target.value)} />
              </div>
              <div className="bt-field">
                <label>Sell above RSI (overbought)</label>
                <input className="bt-input" type="number" placeholder="e.g. 60"
                  value={params.overboughtThreshold} onChange={e => setParam("overboughtThreshold", e.target.value)} />
              </div>
              <p className="bt-hint">Buys when RSI drops into oversold, sells when RSI rises into overbought.</p>
            </>}
          </div>

          <div className="bt-config-section">
            <label className="bt-label">Starting Capital</label>
            <div className="bt-input-prefix">
              <span>$</span>
              <input className="bt-input" type="number" value={capital}
                onChange={e => setCapital(e.target.value)} placeholder="10000" />
            </div>
          </div>

          <div className="bt-config-section">
            <label className="bt-label">Time Period</label>
            <div className="bt-period-pills">
              {PERIODS.map(p => (
                <button key={p.value}
                  className={`bt-pill${period === p.value ? " active" : ""}`}
                  onClick={() => setPeriod(p.value)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <button className="bt-run-btn" onClick={handleRun} disabled={loading}>
            {loading ? <><span className="bt-spinner" /> Fetching data…</> : "▶ Run Backtest"}
          </button>

          {error && <div className="bt-error">{error}</div>}
        </aside>

        {/* ── Results Panel ──────────────────────────────── */}
        <main className="bt-results">
          {!hasResult && !loading && (
            <div className="bt-empty">
              {!strategyInfoDismissed && STRATEGY_INFO[strategyType] ? (
                <div className="bt-strategy-card">
                  <button className="bt-strategy-card-close" onClick={() => setStrategyInfoDismissed(true)} aria-label="Dismiss">×</button>
                  <div className="bt-strategy-card-title">{STRATEGY_INFO[strategyType].title}</div>
                  <p className="bt-strategy-card-summary">{STRATEGY_INFO[strategyType].summary}</p>
                  <ul className="bt-strategy-card-bullets">
                    {STRATEGY_INFO[strategyType].bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              ) : (
                <>
                  <div className="bt-empty-icon">◎</div>
                  <p>Configure your strategy and hit <strong>Run Backtest</strong> to see results.</p>
                </>
              )}
            </div>
          )}

          {loading && (
            <div className="bt-empty">
              <div className="bt-spinner bt-spinner-lg" />
              <p>Fetching historical data for <strong>{symbol}</strong>…</p>
            </div>
          )}

          {hasResult && <>
            {/* Summary stats */}
            <div className="bt-stats-grid">
              <div className={`bt-stat-card ${statPositive ? "positive" : "negative"}`}>
                <div className="bt-stat-label">Total Return</div>
                <div className="bt-stat-value">{fmtPct(backtestCore.stats.totalReturn)}</div>
                <div className="bt-stat-sub">{fmtCurrency(parseFloat(capital))} → {fmtCurrency(backtestCore.finalValue)}</div>
              </div>
              <div className="bt-stat-card">
                <div className="bt-stat-label">Completed Trades</div>
                <div className="bt-stat-value">{backtestCore.stats.numTrades}</div>
                <div className="bt-stat-sub">{backtestCore.openPosition ? "1 open position" : "No open positions"}</div>
              </div>
              <div className="bt-stat-card">
                <div className="bt-stat-label">Win Rate</div>
                <div className="bt-stat-value">{backtestCore.stats.winRate}{backtestCore.stats.winRate !== "—" ? "%" : ""}</div>
                <div className="bt-stat-sub">of closed trades</div>
              </div>
              <div className="bt-stat-card negative-light">
                <div className="bt-stat-label">Max Drawdown</div>
                <div className="bt-stat-value">-{backtestCore.stats.maxDrawdown}%</div>
                <div className="bt-stat-sub">peak-to-trough</div>
              </div>
            </div>

            {/* Price chart */}
            <div className="bt-chart-card">
              <div className="bt-chart-header">
                <span className="bt-chart-title">{symbol} Price + Trade Signals</span>
                <div className="bt-chart-legend">
                  <span className="bt-leg bt-leg-buy">▲ Buy</span>
                  <span className="bt-leg bt-leg-sell">▼ Sell</span>
                </div>
              </div>

              {/* Indicator toolbar */}
              <IndicatorToolbar activeIndicators={activeIndicators} onToggle={toggleIndicator} />

              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={tc.primary} stopOpacity={0.12} />
                      <stop offset="95%" stopColor={tc.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tc.line} />
                  <XAxis dataKey="date" tick={{ fill: tc.inkFaint, fontSize: 11 }}
                    tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: tc.inkFaint, fontSize: 11 }}
                    tickLine={false} axisLine={false} tickFormatter={v => `$${v}`}
                    domain={["auto", "auto"]} width={68} />
                  <Tooltip content={<PriceTooltip />} />

                  {/* Price area */}
                  <Area type="monotone" dataKey="price" stroke={tc.primary} strokeWidth={1.5}
                    fill="url(#priceGrad)" dot={(props) => <TradeDot {...props} primaryColor={tc.primary} />} activeDot={{ r: 4, fill: tc.primary }} />

                  {/* Overlay MA/EMA lines */}
                  {overlayInds.map(ind => (
                    <Line key={ind.key} type="monotone" dataKey={ind.key}
                      stroke={ind.color} strokeWidth={1.5} dot={false}
                      activeDot={{ r: 3, fill: ind.color }}
                      connectNulls={false} strokeOpacity={0.9} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* RSI panel — only shown when toggled */}
            {showRSI && (
              <div className="bt-chart-card">
                <div className="bt-chart-header">
                  <span className="bt-chart-title" style={{ color: "#818cf8" }}>RSI (14)</span>
                  <div className="bt-rsi-zones">
                    <span className="bt-rsi-tag overbought">Overbought &gt;70</span>
                    <span className="bt-rsi-tag oversold">Oversold &lt;30</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tc.line} />
                    <XAxis dataKey="date" tick={{ fill: tc.inkFaint, fontSize: 11 }}
                      tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis domain={[0, 100]} ticks={[0, 30, 50, 70, 100]}
                      tick={{ fill: tc.inkFaint, fontSize: 11 }}
                      tickLine={false} axisLine={false} width={35} />
                    <Tooltip content={<RSITooltip />} />
                    {/* Overbought / oversold reference bands */}
                    <ReferenceLine y={70} stroke="rgba(244,63,94,0.4)"  strokeDasharray="4 3" />
                    <ReferenceLine y={30} stroke="rgba(34,197,94,0.4)"  strokeDasharray="4 3" />
                    <ReferenceLine y={50} stroke="rgba(255,255,255,0.1)" strokeDasharray="2 4" />
                    <Line type="monotone" dataKey="rsi14" stroke="#818cf8" strokeWidth={1.5}
                      dot={false} activeDot={{ r: 3, fill: "#818cf8" }} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Portfolio value chart */}
            <div className="bt-chart-card">
              <div className="bt-chart-header">
                <span className="bt-chart-title">Portfolio Value Over Time</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="portGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#818cf8" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tc.line} />
                  <XAxis dataKey="date" tick={{ fill: tc.inkFaint, fontSize: 11 }}
                    tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: tc.inkFaint, fontSize: 11 }}
                    tickLine={false} axisLine={false}
                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                    domain={["auto", "auto"]} width={52} />
                  <Tooltip formatter={v => [fmtCurrency(v), "Portfolio"]}
                    contentStyle={{ background: tc.surface, border: `1px solid ${tc.line}`, borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: tc.inkFaint }} />
                  <ReferenceLine y={parseFloat(capital)} stroke={tc.line}
                    strokeDasharray="4 4"
                    label={{ value: "Start", fill: tc.inkFaint, fontSize: 10 }} />
                  <Area type="monotone" dataKey="portfolioValue" stroke="#818cf8" strokeWidth={1.5}
                    fill="url(#portGrad)" dot={false} activeDot={{ r: 4, fill: "#818cf8" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Trades table */}
            {backtestCore.trades.length > 0 && (
              <div className="bt-chart-card">
                <div className="bt-chart-header">
                  <span className="bt-chart-title">Trade Log</span>
                  <span className="bt-trade-count">{backtestCore.trades.length} entries</span>
                </div>
                <div className="bt-table-wrap">
                  <table className="bt-table">
                    <thead>
                      <tr><th>#</th><th>Type</th><th>Date</th><th>Price</th><th>Shares</th><th>P/L</th><th>Return</th></tr>
                    </thead>
                    <tbody>
                      {backtestCore.trades.map((t, i) => (
                        <tr key={i}>
                          <td className="bt-td-muted">{i + 1}</td>
                          <td><span className={`bt-badge ${t.type === "BUY" ? "buy" : "sell"}`}>{t.type === "BUY" ? "▲ BUY" : "▼ SELL"}</span></td>
                          <td>{t.date}</td>
                          <td className="bt-mono">{fmtCurrency(t.price)}</td>
                          <td className="bt-mono">{t.shares?.toLocaleString()}</td>
                          <td className={`bt-mono ${t.profit != null ? (t.profit >= 0 ? "positive" : "negative") : ""}`}>
                            {t.profit != null ? fmtCurrency(t.profit) : "—"}
                          </td>
                          <td className={`bt-mono ${t.returnPct != null ? (parseFloat(t.returnPct) >= 0 ? "positive" : "negative") : ""}`}>
                            {t.returnPct != null ? `${t.returnPct}%` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>}
        </main>
      </div>
    </div>
  );
};

export default BacktestPage;
